import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Map as MapIcon,
  School as SchoolIcon,
  Quiz as QuizIcon,
  Insights as InsightsIcon,
  PlayArrow as PlayIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { learningAchievements } from '../../data/ancientAliensLearningData';

interface LearningDemoProps {
  onStartLearning: () => void;
}

const LearningDemo: React.FC<LearningDemoProps> = ({ onStartLearning }) => {
  const theme = useTheme();
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);

  const features = [
    {
      id: 'mindmap',
      title: 'Interactive Mind Map',
      description: 'Navigate through interconnected learning concepts with visual progress tracking',
      icon: <MapIcon />,
      color: theme.palette.primary.main,
      details: [
        'Zoom and pan through the knowledge graph',
        'Visual progress indicators for each node',
        'Prerequisite-based learning paths',
        'Real-time mastery level tracking'
      ]
    },
    {
      id: 'assessment',
      title: 'Learning Style Assessment',
      description: 'Discover your optimal learning approach through personalized questionnaires',
      icon: <PsychologyIcon />,
      color: theme.palette.secondary.main,
      details: [
        'VARK learning style detection',
        'Cognitive load assessment',
        'Motivation profile analysis',
        'Personalized learning recommendations'
      ]
    },
    {
      id: 'resources',
      title: 'Smart Resource Recommendations',
      description: 'AI-powered suggestions based on your learning style and progress',
      icon: <SchoolIcon />,
      color: theme.palette.success.main,
      details: [
        'Learning style matching algorithm',
        'Difficulty progression tracking',
        'Time-based resource filtering',
        'Multiple content formats (text, video, interactive)'
      ]
    },
    {
      id: 'quiz',
      title: 'Adaptive Quiz System',
      description: 'Personalized quizzes that adapt to your learning pace and style',
      icon: <QuizIcon />,
      color: theme.palette.warning.main,
      details: [
        'Dynamic question generation',
        'Learning style-optimized questions',
        'Real-time progress tracking',
        'Immediate feedback and explanations'
      ]
    },
    {
      id: 'analytics',
      title: 'Learning Analytics Dashboard',
      description: 'Track your progress with detailed insights and achievement system',
      icon: <InsightsIcon />,
      color: theme.palette.info.main,
      details: [
        'Progress visualization',
        'Learning velocity tracking',
        'Achievement system',
        'Strengths and weaknesses analysis'
      ]
    }
  ];

  const sampleTopics = [
    'Foundations of Ancient Alien Theory',
    'Great Pyramids of Giza',
    'Puma Punku & Tiwanaku',
    'Sumerian Cuneiform Texts',
    'Vedic Sanskrit Texts',
    'Scientific Criticism & Skepticism'
  ];

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
          🧠 Ancient Aliens Learning System
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
          A comprehensive mind map learning platform with personalized assessments and adaptive resources
        </Typography>
        
        <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
          <Typography variant="body1">
            <strong>Demo Features:</strong> This learning system demonstrates advanced educational technology including 
            interactive mind maps, learning style assessment, AI-powered resource recommendations, adaptive quizzes, 
            and comprehensive learning analytics - all using ancient aliens as the learning topic.
          </Typography>
        </Alert>

        <Button
          variant="contained"
          size="large"
          onClick={onStartLearning}
          startIcon={<PlayIcon />}
          sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
        >
          Start Learning Journey
        </Button>
      </Box>

      {/* Features Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {features.map((feature) => (
          <Grid item xs={12} md={6} lg={4} key={feature.id}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                border: selectedFeature === feature.id ? `2px solid ${feature.color}` : '1px solid',
                borderColor: selectedFeature === feature.id ? feature.color : 'divider',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8]
                }
              }}
              onClick={() => setSelectedFeature(selectedFeature === feature.id ? null : feature.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    color: feature.color, 
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {feature.description}
                </Typography>

                {selectedFeature === feature.id && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Key Features:
                    </Typography>
                    <List dense>
                      {feature.details.map((detail, index) => (
                        <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon sx={{ fontSize: 16, color: feature.color }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={detail}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Learning Topics Preview */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon />
          Learning Topics Covered
        </Typography>
        
        <Grid container spacing={2}>
          {sampleTopics.map((topic, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Chip 
                label={topic}
                variant="outlined"
                sx={{ 
                  width: '100%',
                  justifyContent: 'flex-start',
                  height: 'auto',
                  py: 1,
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    textAlign: 'left'
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Sample Achievements */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckIcon />
          Achievement System
        </Typography>
        
        <Grid container spacing={2}>
          {learningAchievements.slice(0, 3).map((achievement) => (
            <Grid item xs={12} sm={6} md={4} key={achievement.id}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {achievement.icon}
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {achievement.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {achievement.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Chip 
                      label={achievement.rarity} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                    <Chip 
                      label={`${achievement.points} pts`} 
                      size="small" 
                      color="secondary"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Technical Features */}
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          🚀 Technical Implementation
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Frontend Technologies
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon /></ListItemIcon>
                <ListItemText primary="React 18 with TypeScript" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon /></ListItemIcon>
                <ListItemText primary="Material-UI for responsive design" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon /></ListItemIcon>
                <ListItemText primary="D3.js for interactive visualizations" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon /></ListItemIcon>
                <ListItemText primary="React Router for navigation" />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Learning Features
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon /></ListItemIcon>
                <ListItemText primary="Adaptive learning algorithms" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon /></ListItemIcon>
                <ListItemText primary="Learning style detection" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon /></ListItemIcon>
                <ListItemText primary="Progress tracking and analytics" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon /></ListItemIcon>
                <ListItemText primary="Gamification elements" />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default LearningDemo;
